import React, { useCallback, useEffect, useState, useRef } from 'react';
import { AIChatMessage, AIChatPanel } from './AIChatPanel/AIChatPanel';
export interface PhoebePromptDetail {
  prompt: string;
  response?: string;
  suggestedQuestions?: string[];
  suggestionResponses?: Record<string, string>;
}
interface PhoebeRouteContext {
  greeting: string;
  suggestedQuestions: string[];
  suggestionResponses: Record<string, string>;
}
export function openPhoebeChat(detail: PhoebePromptDetail) {
  window.dispatchEvent(new CustomEvent<PhoebePromptDetail>('phoebe:prompt', {
    detail
  }));
}
const DEFAULT_GREETING = 'Hi there, how can I help?';
const PAGE_TOUR_QUESTION = 'Give me a quick tour of this page';
const PLATFORM_TOUR_QUESTION = 'Tour the whole platform';
const DEFAULT_CONTEXT: PhoebeRouteContext = {
  greeting: DEFAULT_GREETING,
  suggestedQuestions: ['What can you help me with?', 'Give me a quick summary', 'What should I focus on?', 'Show me recent activity'],
  suggestionResponses: {
    'What can you help me with?': 'I can help you understand learner activity, identify priorities, plan follow-up, and navigate the work on this page. What would you like to explore?',
    'Give me a quick summary': 'I can summarize the key information on this screen and call out what needs attention first. Tell me whether you would like an organization, learner, skill, or task-focused summary.',
    'What should I focus on?': 'Start with the learners or skills showing the largest performance or engagement gap, then choose one clear follow-up action and a measure to review next week.',
    'Show me recent activity': 'I can help surface recent learner progress, assessment activity, alerts, and report updates. Which area would you like to review?'
  }
};
const REPORT_BUILDER_GREETING = 'What would you like to do with reports?';
const REPORT_BUILDER_SUGGESTIONS = ['Help me build a custom report', 'Create a learner progress report', 'Analyze assessment results', 'Show completion trends'];
const REPORT_BUILDER_RESPONSES: Record<string, string> = {
  'Help me build a custom report': 'Absolutely. Let’s define the report together — what would you like to include?',
  'Create a learner progress report': 'I can structure a learner progress report around learner names, enrolled skills, completion status, progress percentage, and last activity. Which skills and date range should it cover?',
  'Analyze assessment results': 'I can focus on assessment attempts, average scores, pass rates, missed concepts, and learners who may need support. Which assessment or skill should we start with?',
  'Show completion trends': 'I can prepare a completion report with completion rates, certificates earned, overdue learners, and changes over time. Would you like an organization-wide summary or a breakdown by team or skill?'
};
const CUSTOM_REPORT_SUGGESTIONS = ['Learner progress and activity', 'Skills and completion status', 'Assessment scores and pass rates', 'Choose learners, teams, and dates'];
const CUSTOM_REPORT_RESPONSES: Record<string, string> = {
  'Learner progress and activity': 'I’ll include learner names, enrollment, progress percentage, last activity, and inactive learners. Would you like this grouped by individual learner, team, or skill?',
  'Skills and completion status': 'I’ll include skill enrollment, completion status, certificates earned, and outstanding modules. Would you like a high-level summary or a detailed learner-level breakdown?',
  'Assessment scores and pass rates': 'I’ll include assessment attempts, average scores, pass rates, commonly missed concepts, and learners who may need support. Which assessment or skill should be the focus?',
  'Choose learners, teams, and dates': 'Tell me the learner group or team, the skills to include, and the date range. I’ll use those filters to shape the report.'
};
const ROUTE_CONTEXTS: Record<string, PhoebeRouteContext> = {
  '/admin': {
    greeting: 'What would you like to do for your organization?',
    suggestedQuestions: ['Summarize organization performance', 'Find learners who need follow-up', 'Plan a learner reminder', 'Recommend my next admin action'],
    suggestionResponses: {
      'Summarize organization performance': 'Your dashboard can bring together registration, participation, and completion signals. I can help you turn those into a concise leadership update with the biggest gain, the largest gap, and one recommended next step.',
      'Find learners who need follow-up': 'Prioritize learners who have not completed registration, have been inactive recently, or are close to finishing a skill. I can help you segment the list and tailor the outreach for each group.',
      'Plan a learner reminder': 'A useful reminder is specific, supportive, and easy to act on. I can draft a message that references the learner’s next step, offers help, and includes a clear completion date or support option.',
      'Recommend my next admin action': 'Start with the highest-impact bottleneck: learners who are registered but inactive or nearly complete. A focused reminder campaign and one follow-up measure will give you a clear improvement signal.'
    }
  },
  '/analytics': {
    greeting: 'What would you like to understand about performance?',
    suggestedQuestions: ['Explain the performance trends', 'Find at-risk learners', 'Compare skill performance', 'Suggest a data-driven next step'],
    suggestionResponses: {
      'Explain the performance trends': 'I can help interpret score, mastery, attempt, and confidence patterns. Look for a trend that is both sizable and persistent, then compare it by skill or learner group to understand where action will matter most.',
      'Find at-risk learners': 'At-risk learners often combine declining scores, repeated attempts, low activity, or high-confidence incorrect answers. I can help identify the strongest signals and shape an appropriate check-in plan.',
      'Compare skill performance': 'Compare average score, mastery, confident errors, and attempts across skills. A skill with lower mastery and more confident errors is usually where learners need the most preparation and support.',
      'Suggest a data-driven next step': 'Choose one struggling learner group or skill, define a targeted support action, and track the relevant score, completion, or engagement metric over the next one to two weeks.'
    }
  },
  '/assessment': {
    greeting: 'How can I help you manage learners?',
    suggestedQuestions: ['Help me add a learner', 'Plan a bulk learner import', 'Choose skills and pathways', 'Review learner assignment needs'],
    suggestionResponses: {
      'Help me add a learner': 'To add a learner, gather their name and email, then choose the skills or pathways that fit their role. I can help you decide what to assign before you send the invitation.',
      'Plan a bulk learner import': 'For a smooth bulk import, confirm that each row has a first name, last name, and unique email address. Then group learners by shared pathway or skill needs so you can assign training consistently after import.',
      'Choose skills and pathways': 'Pathways work well for structured role-based learning, while individual skills are useful for targeted gaps. Tell me the learner group and desired outcome, and I can recommend a focused assignment mix.',
      'Review learner assignment needs': 'Start with learners who are pending, unassigned, inactive, or not started. Their status points to the right action: invite, assign learning, send a reminder, or offer direct support.'
    }
  },
  '/feedback': {
    greeting: 'What would you like to do with reports?',
    suggestedQuestions: ['Help me build a custom report', 'Create a learner progress report', 'Analyze assessment results', 'Show completion trends'],
    suggestionResponses: REPORT_BUILDER_RESPONSES
  },
  '/profile': {
    greeting: 'What would you like to update in your profile?',
    suggestedQuestions: ['Update my personal information', 'Help with password security', 'Choose language and time zone', 'Review notification settings'],
    suggestionResponses: {
      'Update my personal information': 'You can update your name and profile photo here. Your email address is managed separately, so contact support if you need to change the address associated with your account.',
      'Help with password security': 'Use a unique password with at least eight characters, an uppercase letter, a number, and a special character. I can help you confirm the password requirements or plan a safer update.',
      'Choose language and time zone': 'Set your language to the one you use most and choose the time zone where you work. That keeps dates, schedules, and platform content aligned with your day-to-day workflow.',
      'Review notification settings': 'Open the Settings tab to choose which updates reach you on the web, by email, or both. Keep time-sensitive learner and grading notifications on, then reduce lower-priority updates.'
    }
  },
  '/alerts': {
    greeting: 'What would you like to do with your notifications?',
    suggestedQuestions: ['Summarize what needs attention', 'Explain this maintenance window', 'Help me reply to support', 'Keep my inbox manageable'],
    suggestionResponses: {
      'Summarize what needs attention': 'Two notifications have deadlines: the single sign-on certificate expires August 18, and only 12 learner seats remain. Both will block people from signing in or being invited if they are not resolved.',
      'Explain this maintenance window': 'Maintenance runs Saturday, August 8 from 2:00 AM to 6:00 AM EST. Assessments and reporting are unavailable during that window, so it is worth telling learners who typically study on weekend mornings.',
      'Help me reply to support': 'Confirm whether the issue is resolved on your side, include the ticket number, and note how many learners were affected and when. That detail usually prevents another round trip.',
      'Keep my inbox manageable': 'Read and archive informational notices such as invoices and policy updates, and keep anything with a deadline or a security implication active until it is resolved.'
    }
  },
  '/insights-actions': {
    greeting: 'What would you like to do with these insights?',
    suggestedQuestions: ['Help me prioritize these insights', 'Explain a learning pattern', 'Plan an intervention', 'Find what is helping learners succeed'],
    suggestionResponses: {
      'Help me prioritize these insights': 'Prioritize insights by learner impact, severity, and how quickly you can act. Address critical patterns affecting many learners first, then schedule focused support for individual learners who need it.',
      'Explain a learning pattern': 'I can help connect the metrics, signals, and learner behavior behind an insight. Start with the skill or learner you are reviewing, and we can separate the symptom from the likely cause.',
      'Plan an intervention': 'An effective intervention pairs one focused action with a measurable outcome. For example, point a learner group to the practice resources they are missing or host a study session, then review scores, attempts, or completion after one week.',
      'Find what is helping learners succeed': 'Look for the learner behaviors behind stronger pass rates — using the available resources, spacing out attempts, or preparing before a first attempt. Encouraging those same habits with struggling learners is usually the fastest win.'
    }
  },
  '/bulk-upload-results': {
    greeting: 'How can I help you finish this learner import?',
    suggestedQuestions: ['Review import validation issues', 'Check learners ready to import', 'Assign pathways to new learners', 'Choose skills for this cohort'],
    suggestionResponses: {
      'Review import validation issues': 'Resolve rows with missing or invalid information before confirming the import. Check email formatting, duplicate accounts, and required names first so the learner list stays clean.',
      'Check learners ready to import': 'Review the learners marked ready alongside any existing accounts that will be updated. This helps ensure the import creates the right records without duplicating people.',
      'Assign pathways to new learners': 'Choose pathways when the incoming learners share a role or learning journey. A consistent pathway assignment makes onboarding clearer and gives you a cohesive progress view later.',
      'Choose skills for this cohort': 'Choose individual skills for a focused cohort need or when learners require a targeted supplement. I can help balance the assignment so it is relevant without overwhelming new learners.'
    }
  }
};
export function openReportBuilderPhoebe() {
  window.dispatchEvent(new CustomEvent('phoebe:report-builder'));
}
const defaultResponse = 'I can help you interpret learner patterns, prioritize support, and turn insights into next steps. Based on the available data, I recommend focusing first on the highest-impact learner or skill gap, then setting a clear follow-up measure for the next two weeks.';
export function GlobalPhoebeChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>(DEFAULT_CONTEXT.suggestedQuestions);
  const [suggestionResponses, setSuggestionResponses] = useState<Record<string, string>>(DEFAULT_CONTEXT.suggestionResponses);
  const [greeting, setGreeting] = useState(DEFAULT_GREETING);
  const isTypingRef = useRef(false);
  const nextIdRef = useRef(1);
  const replyTimerRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const stopCurrentReply = useCallback(() => {
    if (replyTimerRef.current) window.clearTimeout(replyTimerRef.current);
    replyTimerRef.current = null;
    isTypingRef.current = false;
    setIsTyping(false);
  }, []);
  const replyTo = useCallback((prompt: string, response = defaultResponse) => {
    if (!prompt.trim() || isTypingRef.current) return;
    const id = nextIdRef.current;
    nextIdRef.current += 2;
    setMessages(current => [...current, {
      id,
      role: 'user',
      content: prompt.trim()
    }]);
    setInputValue('');
    setIsOpen(true);
    // Any interaction with Phoebe should retire the intro invitation
    window.dispatchEvent(new CustomEvent('phoebe:chat-opened'));
    isTypingRef.current = true;
    setIsTyping(true);
    replyTimerRef.current = window.setTimeout(() => {
      setMessages(current => [...current, {
        id: id + 1,
        role: 'assistant',
        content: response
      }]);
      replyTimerRef.current = null;
      isTypingRef.current = false;
      setIsTyping(false);
    }, 850);
  }, []);
  const startContext = useCallback((context: PhoebeRouteContext) => {
    stopCurrentReply();
    setMessages([]);
    setInputValue('');
    setGreeting(context.greeting);
    setSuggestedQuestions([PAGE_TOUR_QUESTION, PLATFORM_TOUR_QUESTION, ...context.suggestedQuestions.slice(0, 2)]);
    setSuggestionResponses({
      ...context.suggestionResponses,
      [PAGE_TOUR_QUESTION]: 'I’ll highlight the main area of this page and explain what you can do here.',
      [PLATFORM_TOUR_QUESTION]: 'I’ll walk you through the Admin Hub, Analytics, Reports, and User Management from start to finish.'
    });
    setIsOpen(true);
    window.dispatchEvent(new CustomEvent('phoebe:chat-opened'));
  }, [stopCurrentReply]);
  const startReportBuilder = useCallback(() => {
    startContext({
      greeting: REPORT_BUILDER_GREETING,
      suggestedQuestions: REPORT_BUILDER_SUGGESTIONS,
      suggestionResponses: REPORT_BUILDER_RESPONSES
    });
  }, [startContext]);
  useEffect(() => {
    const handlePrompt = (event: Event) => {
      const {
        prompt,
        response,
        suggestedQuestions,
        suggestionResponses
      } = (event as CustomEvent<PhoebePromptDetail>).detail;
      setGreeting(DEFAULT_GREETING);
      setSuggestedQuestions(suggestedQuestions || []);
      setSuggestionResponses(suggestionResponses || {});
      replyTo(prompt, response);
    };
    window.addEventListener('phoebe:prompt', handlePrompt);
    window.addEventListener('phoebe:report-builder', startReportBuilder);
    return () => {
      window.removeEventListener('phoebe:prompt', handlePrompt);
      window.removeEventListener('phoebe:report-builder', startReportBuilder);
      stopCurrentReply();
    };
  }, [replyTo, startReportBuilder, stopCurrentReply]);
  const handleSuggestedQuestion = (question: string) => {
    const response = suggestionResponses[question];
    if (question === PAGE_TOUR_QUESTION) {
      setIsOpen(false);
      window.dispatchEvent(new CustomEvent<string>('phoebe:page-tour', {
        detail: window.location.pathname
      }));
      return;
    }
    if (question === PLATFORM_TOUR_QUESTION) {
      setIsOpen(false);
      window.dispatchEvent(new CustomEvent('phoebe:platform-tour'));
      return;
    }
    if (question === 'Help me build a custom report') {
      setSuggestedQuestions(CUSTOM_REPORT_SUGGESTIONS);
      setSuggestionResponses(CUSTOM_REPORT_RESPONSES);
    } else {
      setSuggestedQuestions([]);
      setSuggestionResponses({});
    }
    replyTo(question, response);
  };
  const handleSend = () => {
    setSuggestedQuestions([]);
    setSuggestionResponses({});
    replyTo(inputValue);
  };
  const handleOpen = () => {
    const pathname = window.location.pathname;
    startContext(ROUTE_CONTEXTS[pathname] || DEFAULT_CONTEXT);
  };
  return <AIChatPanel isOpen={isOpen} onOpen={handleOpen} onClose={() => setIsOpen(false)} messages={messages} inputValue={inputValue} isTyping={isTyping} onInputChange={setInputValue} onSend={handleSend} onSuggestedQuestion={handleSuggestedQuestion} suggestedQuestions={suggestedQuestions} greeting={greeting} data-id="element-1488" data-mp="tvx66ufsc5ae" />;
}